import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  Card,
  Divider,
  Checkbox,
  Dialog,
  BUTTON_VARIANTS,
  TableHeader,
  MenuListItem,
  TextRenderer,
  TableContent,
  usePreventTableScrollingRef,
  TableContainer,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
} from 'cdk-radial';
import { AgGridReact } from 'ag-grid-react';
import {
  setSelectedRequiredField,
  updateSelectedFields,
  setISVFields,
  setSelectedApi,
  setISVRequiredFields,
  setFieldSearchInput,
} from '../../../../../redux/connectedComponent/apiFields/apiFields.slice';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import classNames from 'classnames';
import { CONSTANTS } from '../../manageIntegrations/v2/constants';
import { requiredApiFieldsForSpec } from '../../../../../constants/constants';
import { ApiFieldNameTooltip } from '../../../../UI/ApiFieldNameTooltip';

const StyledDialog = styled(Dialog)`
  max-width: 510px;
`;

export const getIsvSelectedFieldsByAPIId = apiData => {
  if(!Array.isArray(apiData)) {
    throw new Error('Invalid input: array expected');
  }
  return Object.fromEntries(
    apiData.map(({ apiId, fields }) => [apiId, fields.map(f => f.name)])
  );
};

export const getIsvSelectedRequiredFieldsByAPIId = apiData => {
  if(!Array.isArray(apiData)) {
    throw new Error('Invalid input: array expected');
  }
  
  return Object.fromEntries(
    apiData.map(({ apiId, fields }) => [
      apiId,
      fields.filter((f) => f.required === true)
      .map((f) => f.name),
    ])
  );
};

export default function ManageFields(props) {
  const [gridApi, setGridApi] = useState(null);
  const [gridData, setGridData] = useState();
  const [data, setData] = useState([]);
  const [apiFields, setApiFields] = useState([]);
  const [totalRows, setTotalRows] = useState();
  const componentMainClass = 'apiEditFields__table';
  const agGridThemeClass = 'ag-theme-balham';
  const tableClass = classNames(componentMainClass, agGridThemeClass);
  const dispatch = useDispatch();
  const { addToast, removeToast } = props;

  const tableContentRef = usePreventTableScrollingRef();

  const frameworkComponentsExtended = {
    textRenderer: TextRenderer,
  };

  const state = useSelector((state) => state);
  const { verticalScopingApisList } = state && state?.selectDMSSlice;
  const {
    selectedLocationName,
    updatedFieldSelection,
    selectedApiDetails,
    fieldSearchInput,
  } = state?.apiFieldsSlice;

  const getAllInstanceFieldMerged = (apiInstances) => {
    const uniqueFieldObj = {};
    for (const instance of apiInstances){
      for (const fieldObj of instance) {
        let fieldName = fieldObj.name;
        let currentField = uniqueFieldObj[fieldName];
        // add key values to the uniqueFieldObj for 1st instance
        if(!currentField){
          uniqueFieldObj[fieldName] = {...fieldObj};
        } else {
          // if example is missing check for next instance and update
          if (!currentField.description && fieldObj.description) {
            currentField = { ...fieldObj };
          }
          if (fieldObj.description && fieldObj.example) {
            currentField = { ...fieldObj };
          }
          uniqueFieldObj[fieldName] = { ...currentField };
        }
      }
    }
    return Object.values(uniqueFieldObj);
}


useEffect(()=>{
  tableContentRef?.current?.api?.refreshCells({
    force: true
  });
},[updatedFieldSelection])

  const subscribedApiIds = useMemo(() => {
    return updatedFieldSelection
      ? Object.keys(updatedFieldSelection).sort().join(',')
      : '';
  }, [updatedFieldSelection]);

  useEffect(() => {
    if (verticalScopingApisList && verticalScopingApisList?.length) {
      const mapApiNamesAndFields = verticalScopingApisList.map((f) => {
        const apiInstances = Object.values(f?.apiFieldSelections?.instances);
        const mergeAllInstancesFields = getAllInstanceFieldMerged(apiInstances);

        mergeAllInstancesFields.sort((a, b) => {
          const field1 = a?.name?.toUpperCase();
          const field2 = b?.name?.toUpperCase();

          if (field1 < field2) {
            return -1;
          }
          if (field1 > field2) {
            return 1;
          }
          return 0;
        });
        return {
          apiName: f.name,
          fields: mergeAllInstancesFields,
          apiType: f.apiType,
          apiId: f.id,
        };
      });
      const mapApiFields =
        Array.isArray(mapApiNamesAndFields) &&
        mapApiNamesAndFields.map((f) => {
          const filterRequiredFields =
            Array.isArray(f.fields) &&
            f.fields.filter(
              (field) => !requiredApiFieldsForSpec.has(field.name)
            );

          return { ...f, fields: filterRequiredFields };
        });

      // Filter APIs based on current location's active subscriptions
      const subscribedApiIdsArray = updatedFieldSelection ? Object.keys(updatedFieldSelection) : [];
      const filteredApiFields = mapApiFields.filter((api) =>
        subscribedApiIdsArray.includes(api.apiId)
      );

      // Use filtered list if location is selected, otherwise show all
      const apisToDisplay = subscribedApiIdsArray.length > 0 ? filteredApiFields : mapApiFields;
      setApiFields(apisToDisplay);
      
      if (apisToDisplay.length > 0) {
        const gridDataForInitialRender = apisToDisplay[0];
        setGridData(gridDataForInitialRender);
        dispatch(
          setSelectedApi({
            apiName: gridDataForInitialRender.apiName,
            apiId: gridDataForInitialRender.apiId,
          }),
        );
      }
      
      dispatch(setISVFields(getIsvSelectedFieldsByAPIId(mapApiNamesAndFields)));
      dispatch(setISVRequiredFields(getIsvSelectedRequiredFieldsByAPIId(mapApiNamesAndFields)));
    }
  }, [verticalScopingApisList, subscribedApiIds]);

  useEffect(() => {
    if (gridData) {
      const apiName = gridData.apiName;
      const apiId = gridData.apiId;
      const apiType = gridData.apiType;
      let prepareGridData = [];
      gridData?.fields?.forEach((f) => {
        prepareGridData.push({
          apiName: apiName,
          fieldName: f.name,
          description: f.description || 'N/A',
          example: f.example || 'N/A',
          required: f.required,
          apiType: apiType,
          apiId: apiId,
        });
      });

      setData(prepareGridData);
    }
  }, [gridData]);

  const CheckboxSelector = ({ data }) => {
    const [showWarningDialog, setShowWarningDialog] = useState(false);

    const state = useSelector((state) => state);
    const { updatedFieldSelection } = state && state?.apiFieldsSlice;

    let filteredApiFields = {};

    for (const [apiId, fields] of Object.entries(updatedFieldSelection)) {
      const filteredFields =
        (fields?.length &&
          fields.filter((f) => !requiredApiFieldsForSpec.has(f))) ||
        [];

      filteredApiFields[apiId] = filteredFields;
    }

    const handleChange = (data) => (event) => {
      if (data?.required === true && event.target.checked === false) {
        setShowWarningDialog(true);
      } else {
        if (event.target.checked === true) {
          dispatch(
            updateSelectedFields({
              operation: 'addField',
              field: data?.fieldName,
              selectedApiId: data?.apiId,
            })
          );
          /*
            We are removing the toast notification if the field array length is zero and we are adding a new field
            Note: we are checking against current state (field length 0) because eventhough we added a field, the new state (field length 1)
            wont be in effect until next render
          */
          if (filteredApiFields[data?.apiId]?.length === 0) {
            removeToast(data?.apiId);
          }
        }
        if (event.target.checked === false) {
          dispatch(
            updateSelectedFields({
              operation: 'removeField',
              field: data?.fieldName,
              selectedApiId: data?.apiId,
            })
          );
          /*
            We are adding a toast notification if the field array length is one and we are removing a new field
          */
          if(filteredApiFields[data?.apiId]?.length === 1 ){
          addToast({
            content:  <span>You need to enable at least one field for <b>{data?.apiName}</b> API to proceed.</span>,
            id: data?.apiId,
            variant: TOAST_VARIANTS.NEGATIVE,
            visibilityDuration: TOAST_VISIBILITY_DURATIONS.INFINITE,
          })
         }
        }
        
      }
    };

    const handleClose = () => {
      setShowWarningDialog(false);
    };

    const handleContinue = () => {
      setShowWarningDialog(false);
      const fieldDetails = {
        apiId: data?.apiId,
        fieldName: data?.fieldName,
        apiName: data?.apiName,
      };
      dispatch(setSelectedRequiredField(fieldDetails));
      dispatch(
        updateSelectedFields({
          operation: 'removeField',
          field: data?.fieldName,
          selectedApiId: data?.apiId,
        })
      );
      /*
        We are adding a toast notification if the field array length is one and we are removing a new field
      */
      if (filteredApiFields[data?.apiId]?.length === 1) {
        addToast({
          content: (
            <span>
              You need to enable at least one field for <b>{data?.apiName}</b>{' '}
              API to proceed.
            </span>
          ),
          id: data?.apiId,
          variant: TOAST_VARIANTS.NEGATIVE,
          visibilityDuration: TOAST_VISIBILITY_DURATIONS.INFINITE,
        });
      }
    };

    const isSelected = (data) => {
      if (updatedFieldSelection[data?.apiId]?.includes(data?.fieldName))
        return true;
      return false;
    };

    return (
      <>
        <Checkbox
          checked={isSelected(data)}
          onChange={handleChange(data)}
          id={`checkbox-${data?.fieldName}`}
          name={`checkbox-${data?.fieldName}`}
          dataTestId={`checkbox-${data?.fieldName}`}
        />
        <ApiFieldNameTooltip {...data} />
        <StyledDialog
          isOpen={showWarningDialog}
          dataTestId='required-field-warning-dialog'
          id="required-field-warning-dialog"
          onClose={handleClose}
          title={'Warning'}
          buttonsProps={[
            {
              id: 'disable-continue-btn',
              dataTestId: 'disable-continue-btn',
              onClick: handleContinue,
              text: 'Continue',
              variant: BUTTON_VARIANTS.TEXT,
            },
          ]}
        >
          You have disabled 1 required field. This action might cause your app
          to no longer function as intended. Do you wish to continue?
        </StyledDialog>
      </>
    );
  };

  const CustomHeaderCheckbox = (props) => {
    const dispatch = useDispatch();
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const state = useSelector((state) => state);
    const {
      updatedFieldSelection,
      selectedApiDetails,
      fieldSearchInput,
      isvFields,
      isvRequiredFields,
    } = state && state?.apiFieldsSlice;
    const currentApiId = selectedApiDetails?.apiId;
    const currentApiName = selectedApiDetails?.apiName;
    const allFieldNames = isvFields[currentApiId] || [];
    const selectedFields = updatedFieldSelection[currentApiId] || [];
    const apiRequiredFields = isvRequiredFields[currentApiId] || [];
    const filteredRows = props?.api?.rowModel?.rowsToDisplay || [];
    const filteredFieldNames = filteredRows.map((row) => row.data.fieldName);

    const isAllSelected =
      fieldSearchInput && fieldSearchInput.length > 0
        ? filteredFieldNames.length > 0 &&
          filteredFieldNames.every((field) => selectedFields.includes(field))
        : allFieldNames.length > 0 &&
          allFieldNames.every((field) => selectedFields.includes(field));

    const handleSelectAll = (isChecked) => {
      if (isChecked) {
        // Select all fields
        dispatch(
          updateSelectedFields({
            operation: 'addAllFields',
            field:
              fieldSearchInput && fieldSearchInput.length > 0
                ? Array.from(
                    new Set([...selectedFields, ...filteredFieldNames])
                  )
                : allFieldNames,
            selectedApiId: currentApiId,
          })
        );
        removeToast(currentApiId);
      } else {
        // Deselect all fields
        dispatch(
          updateSelectedFields({
            operation: 'removeAllFields',
            field:
              fieldSearchInput && fieldSearchInput.length > 0
                ? selectedFields.filter(
                    (field) => !filteredFieldNames.includes(field)
                  )
                : [],
            selectedApiId: currentApiId,
          })
        );
        if (fieldSearchInput === '') {
          addToast({
            content: (
              <span>
                You need to enable at least one field for{' '}
                <b>{currentApiName}</b> API to proceed.
              </span>
            ),
            id: currentApiId,
            variant: TOAST_VARIANTS.NEGATIVE,
            visibilityDuration: TOAST_VISIBILITY_DURATIONS.INFINITE,
          });
        }
      }
    };

    const handleClose = () => {
      setShowWarningDialog(false);
    };

    const handleChange = (event) => {
      const isChecked = event.target.checked;
      if (
        isChecked === false &&
        filteredFieldNames.some((field) => apiRequiredFields.includes(field))
      ) {
        setShowWarningDialog(true);
      } else {
        handleSelectAll(isChecked);
      }
    };

    const handleContinue = () => {
      setShowWarningDialog(false);
      handleSelectAll(false);
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox
          checked={isAllSelected}
          onChange={handleChange}
          id="select-all-checkbox"
          name="select-all-checkbox"
          dataTestId="select-all-checkbox"
        />
        <StyledDialog
          isOpen={showWarningDialog}
          dataTestId='required-field-warning-dialog1'
          id="required-field-warning-dialog"
          onClose={handleClose}
          title={'Warning'}
          buttonsProps={[
            {
              id: 'disable-continue-btn',
              dataTestId: 'disable-continue-btn',
              onClick: handleContinue,
              text: 'Continue',
              variant: BUTTON_VARIANTS.TEXT,
            },
          ]}
        >
          You have disabled all required fields. This action might cause your
          app to no longer function as intended. Do you wish to continue?
        </StyledDialog>
        <span>Fields</span>
      </div>
    );
  };

  const columns = [
    {
      cellRendererFramework: CheckboxSelector,
      field: 'required',
      headerName: 'Fields',
      headerComponentFramework: CustomHeaderCheckbox,
      width: 160,
      sortable: false,
      getQuickFilterText: params => {
        return params.data.fieldName || '';
    }
    },
  ];

  const handleSearchInputChange = (searchInput) => {
    dispatch(setFieldSearchInput(searchInput));
    if (gridApi) {
      gridApi.setQuickFilter(searchInput);
      gridApi.refreshCells({
        force: true,
      });
      const displayedRowsCount = gridApi.getDisplayedRowCount();
      setTotalRows(displayedRowsCount);

      if (searchInput && displayedRowsCount === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  };

  const handleGridReady = (gridParams) => {
    setGridApi(gridParams.api);
    setTotalRows(gridParams.api.getDisplayedRowCount());
    gridParams.api.sizeColumnsToFit();
  };

  const getHeaderTitle = () => {
    return (
      <>
        <div className='manage-fields-api-name-header-container'>
          <div className='manage-fields-api-name-header'>
            <span>{selectedApiDetails?.apiName}</span>
            <span className='manage-fields-api-name-header-subheading'>
              All fields with * are required for optimal App function
            </span>
          </div>
        </div>
      </>
    );
  };
  function handleApiSelection(value) {
    const findSelectedApiDetails =
      apiFields && apiFields.find((f) => f.apiName === value);
    setGridData(findSelectedApiDetails);
    handleSearchInputChange('');
    dispatch(setSelectedApi({apiName: findSelectedApiDetails.apiName, apiId: findSelectedApiDetails.apiId}));
  }

  return (
    <div className="manage-fields-container">
      <div className='manage-fields-container-title'>
      {CONSTANTS.MANAGE_API_FIELDS_DESCRIPTION}
      </div>
      <div className='manage-fields-container-card'>
        <Card>
          <h3 className='manage-fields-container-card-title'>
            {`Edits for ${selectedLocationName}`}
        </h3>

          <Divider></Divider>

          <div className='manage-fields-container-card-div' >
            <div className='api-selection-list'>
              {apiFields.map((f) => (
                <MenuListItem
                  key={f.apiId}
                  onClick={() => handleApiSelection(f.apiName)}
                  dataTestId="menu-list-item"
                  isSelected={selectedApiDetails?.apiName === f?.apiName ? true : false}
                  hideSelectedItemCheckmark={true}
                  className="api-name-selection"
                >
                  <div className='manage-fields-container-card-api-row'>
                    <div className='manage-fields-container-card-api-name'>{f.apiName}</div>
                    <div className='manage-fields-container-card-api-field-count'>{`Fields (${f?.fields?.length})`}</div>
                  </div>
                </MenuListItem>
              ))}
            </div>

            <Card className="api-field-selection-container">
              <TableContainer
                className={tableClass}
                tableLayout={'normal'}
                hideBorder
              >
                <TableHeader
                  headerTitle={getHeaderTitle()}
                  id="manage-fields-table-header"
                  hideBorder
                  isDownloadable={false}
                  isPrintable={false}
                  isSearchable={true}
                  onSearchInputChange={handleSearchInputChange}
                  searchInputValue={fieldSearchInput}
                  searchPlaceholder="Search Fields"
                  dataTestId="manage-fields-table-header"
                />
                <Divider></Divider>
                <TableContent data-testid="table-content" ref={tableContentRef} className="manage-api-fields-table">
                  <AgGridReact
                    frameworkComponents={frameworkComponentsExtended}
                    overlayNoRowsTemplate="No fields to show"
                    overlayLoadingTemplate={'Loading Fields...'}
                    columnDefs={columns}
                    rowHeight={30}
                    onGridReady={handleGridReady}
                    rowData={data}
                    suppressCellFlash={true}
                    suppressAnimationFrame={true}
                    disableStaticMarkup={true}
                  />
                </TableContent>
              </TableContainer>
            </Card>
          </div>
        </Card>
      </div>
    </div>
  );
}
